import React, { useState } from 'react';
import { Box, Button, IconButton, Typography } from "@mui/material";
import { Favorite, FavoriteBorder } from "@mui/icons-material";
import { Link as RouterLink } from "react-router-dom";
import { Product } from "../../api/types";
import { useProductCardStyles } from "./ProductCard.styles";
import { formatMoney } from "../../utils/formatPrice";
import { useI18n } from "../../i8n/useI18n";
import { useWishlist } from "../../hooks/useWishlist";
import { useCart } from "../../hooks/useCart";

interface ProductCardProps {
    product: Product;
    badge?: string;
}

const ProductCard = (props: ProductCardProps) => {
    const { classes, cx } = useProductCardStyles();
    const { t } = useI18n();
    const { isWishlisted, toggleWishlist } = useWishlist();
    const { addItem, isMutating } = useCart();
    const { product } = props;
    const [justAdded, setJustAdded] = useState(false);

    const isRange = product.priceRange.minVariantPrice.amount !== product.priceRange.maxVariantPrice.amount;
    const cheapestVariant = product.variants.find((v) => v.price.amount === product.priceRange.minVariantPrice.amount);
    const compareAt = cheapestVariant?.compareAtPrice ?? undefined;
    const wishlisted = isWishlisted(product.id);
    const addableVariant = cheapestVariant?.availableForSale
        ? cheapestVariant
        : product.variants.find((v) => v.availableForSale);

    const savings =
        compareAt &&
        formatMoney({
            amount: (parseFloat(compareAt.amount) - parseFloat(product.priceRange.minVariantPrice.amount)).toFixed(2),
            currencyCode: product.priceRange.minVariantPrice.currencyCode,
        });

    const handleWishlistClick = (event: React.MouseEvent) => {
        event.preventDefault();
        event.stopPropagation();
        toggleWishlist(product.id);
    };

    const handleAddToCart = async (event: React.MouseEvent) => {
        event.preventDefault();
        event.stopPropagation();
        if (!addableVariant) return;
        await addItem(addableVariant.id, 1);
        setJustAdded(true);
        setTimeout(() => setJustAdded(false), 2000);
    };

    return (
        <Box component={RouterLink} to={`/produkt/${product.handle}`} className={classes.root}>
            <Box className={classes.imageWrapper}>
                {props.badge && <Box className={classes.badge}>{props.badge}</Box>}
                <IconButton
                    className={classes.wishlistButton}
                    size="small"
                    aria-label={wishlisted ? t("common.removeFromWishlist") : t("common.addToWishlist")}
                    onClick={handleWishlistClick}
                >
                    {wishlisted ? (
                        <Favorite className={cx(classes.wishlistIcon, classes.wishlistIconActive)} />
                    ) : (
                        <FavoriteBorder className={classes.wishlistIcon} />
                    )}
                </IconButton>
                {product.featuredImage && (
                    <Box
                        component="img"
                        src={product.featuredImage.url}
                        alt={product.featuredImage.altText ?? product.title}
                        className={classes.image}
                        loading="lazy"
                    />
                )}
            </Box>
            <Box className={classes.info}>
                <Typography className={classes.vendor}>{product.vendor}</Typography>
                <Typography variant="body1" className={classes.title}>{product.title}</Typography>
                <Box className={classes.priceRow}>
                    {isRange && <Typography className={classes.from}>{t("common.from")}</Typography>}
                    <Typography className={classes.price}>{formatMoney(product.priceRange.minVariantPrice)}</Typography>
                </Box>
                {compareAt && (
                    <Box className={classes.pillRow}>
                        <Typography component="span" className={classes.wasPill}>
                            {t("common.wasPrice")} {formatMoney(compareAt)}
                        </Typography>
                        <Typography component="span" className={classes.savePill}>
                            {t("common.youSave")} {savings}
                        </Typography>
                    </Box>
                )}
                <Button
                    className={classes.cta}
                    variant="contained"
                    fullWidth
                    disabled={!addableVariant || isMutating}
                    onClick={handleAddToCart}
                >
                    {justAdded ? t("common.added") : t("common.addToCart")}
                </Button>
            </Box>
        </Box>
    );
};

export default ProductCard;
